import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { FaBuilding, FaUsers, FaQrcode, FaLink, FaCreditCard, FaMoneyBillWave } from 'react-icons/fa';
import KpiCard from '../components/KpiCard/KpiCard.jsx';
import DailyChart from '../components/DailyChart/DailyChart.jsx';
import dashboardApi from '../services/dashboardApi.js';
import assinaturasApi from '../services/assinaturasApi.js';
import { colors } from '../utils/colors.js';

const currencyFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

const STATUS_LABELS = {
  ativa: 'Ativas',
  pausada: 'Pausadas',
  cancelada: 'Canceladas',
  falhada: 'Falhadas',
  pendente: 'Pendentes',
};

const Title = styled.h1`
  font-size: 22px;
  color: ${colors.gray[900]};
  margin: 0 0 24px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  gap: 16px;
`;

const ErrorBox = styled.div`
  background: ${colors.error.light};
  color: ${colors.error.dark};
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const BreakdownBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 24px;
`;

const BreakdownChip = styled.div`
  background: ${colors.white};
  border-radius: 999px;
  padding: 8px 16px;
  font-size: 13px;
  color: ${colors.gray[700]};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  strong {
    color: ${colors.gray[900]};
  }
`;

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');

  const [assinaturasResumo, setAssinaturasResumo] = useState(null);

  const [usuariosDias, setUsuariosDias] = useState(30);
  const [usuariosSerie, setUsuariosSerie] = useState(null);

  const [spacesDias, setSpacesDias] = useState(30);
  const [spacesSerie, setSpacesSerie] = useState(null);

  const [checkinsDias, setCheckinsDias] = useState(30);
  const [checkinsSerie, setCheckinsSerie] = useState(null);

  const [assinaturasDias, setAssinaturasDias] = useState(30);
  const [assinaturasSerie, setAssinaturasSerie] = useState(null);

  useEffect(() => {
    dashboardApi
      .getStats()
      .then(setStats)
      .catch(() => setError('Não foi possível carregar os KPIs.'));
    assinaturasApi
      .resumo()
      .then(setAssinaturasResumo)
      .catch(() => setAssinaturasResumo(null));
  }, []);

  const loadUsuariosSerie = useCallback((dias) => {
    dashboardApi
      .getUsuariosDiario(dias)
      .then(setUsuariosSerie)
      .catch(() => setUsuariosSerie([]));
  }, []);

  const loadSpacesSerie = useCallback((dias) => {
    dashboardApi
      .getSpacesDiario(dias)
      .then(setSpacesSerie)
      .catch(() => setSpacesSerie([]));
  }, []);

  const loadCheckinsSerie = useCallback((dias) => {
    dashboardApi
      .getCheckinsDiario(dias)
      .then(setCheckinsSerie)
      .catch(() => setCheckinsSerie([]));
  }, []);

  const loadAssinaturasSerie = useCallback((dias) => {
    assinaturasApi
      .insightsDiario(dias)
      .then(setAssinaturasSerie)
      .catch(() => setAssinaturasSerie([]));
  }, []);

  useEffect(() => {
    loadUsuariosSerie(usuariosDias);
  }, [usuariosDias, loadUsuariosSerie]);

  useEffect(() => {
    loadSpacesSerie(spacesDias);
  }, [spacesDias, loadSpacesSerie]);

  useEffect(() => {
    loadCheckinsSerie(checkinsDias);
  }, [checkinsDias, loadCheckinsSerie]);

  useEffect(() => {
    loadAssinaturasSerie(assinaturasDias);
  }, [assinaturasDias, loadAssinaturasSerie]);

  return (
    <div>
      <Title>Visão geral da plataforma</Title>
      {error && <ErrorBox>{error}</ErrorBox>}
      <Grid>
        <KpiCard
          icon={FaBuilding}
          label="Spaces"
          value={stats?.spaces_total}
          color={colors.primary[600]}
          bg={colors.primary[100]}
        />
        <KpiCard
          icon={FaUsers}
          label="Usuários"
          value={stats?.usuarios_total}
          color={colors.info.dark}
          bg={colors.info.light}
        />
        <KpiCard
          icon={FaQrcode}
          label="Checkins"
          value={stats?.checkins_total}
          color={colors.success.dark}
          bg={colors.success.light}
        />
        <KpiCard
          icon={FaLink}
          label="Conexões"
          value={stats?.conexoes_total}
          color={colors.warning.dark}
          bg={colors.warning.light}
        />
        <KpiCard
          icon={FaCreditCard}
          label="Assinaturas ativas"
          value={stats?.assinaturas_ativas_total}
          color={colors.primary[700]}
          bg={colors.primary[100]}
        />
        <KpiCard
          icon={FaMoneyBillWave}
          label="Receita mensal recorrente"
          value={assinaturasResumo ? currencyFormatter.format(assinaturasResumo.mrr_centavos / 100) : undefined}
          color={colors.success.dark}
          bg={colors.success.light}
        />
      </Grid>

      {assinaturasResumo && (
        <BreakdownBar>
          {Object.entries(STATUS_LABELS).map(([key, label]) => (
            <BreakdownChip key={key}>
              {label}: <strong>{assinaturasResumo.por_status?.[key] ?? 0}</strong>
            </BreakdownChip>
          ))}
        </BreakdownBar>
      )}

      <ChartsGrid>
        <DailyChart
          title="Usuários cadastrados por dia"
          data={usuariosSerie}
          dias={usuariosDias}
          onChangeDias={setUsuariosDias}
          color={colors.info.main}
        />
        <DailyChart
          title="Spaces criados por dia"
          data={spacesSerie}
          dias={spacesDias}
          onChangeDias={setSpacesDias}
          color={colors.primary[500]}
        />
        <DailyChart
          title="Checkins por dia"
          data={checkinsSerie}
          dias={checkinsDias}
          onChangeDias={setCheckinsDias}
          color={colors.success.main}
        />
        <DailyChart
          title="Novas assinaturas por dia"
          data={assinaturasSerie}
          dias={assinaturasDias}
          onChangeDias={setAssinaturasDias}
          color={colors.primary[700]}
        />
      </ChartsGrid>
    </div>
  );
}
